import { JSX } from "react";
import React from "react";
import { Page, View, Document } from "@react-pdf/renderer";
import { styles, spacing } from "components/Resume/ResumePDF/styles";
import { ResumePDFProfile } from "components/Resume/ResumePDF/ResumePDFProfile";
import { DEFAULT_FONT_COLOR } from "lib/redux/settingsSlice";
import type { Settings, ShowForm } from "lib/redux/settingsSlice";
import type { Resume } from "lib/redux/types";
import { SuppressResumePDFErrorMessage } from "components/Resume/ResumePDF/common/SuppressResumePDFErrorMessage";
import { Profile } from "./Profile";
import { Summary } from "./Summary";
import { Education } from "./Education";
import { Project } from "./Project";
import { WorkExperience } from "./Experience";
import { Skills } from "./Skills";
import { Award } from "./Award";
import { Custom } from "./Custom";
import { Certificate } from "./Certificate";
import { Language } from "./Language";
import { FontSizeProvider } from "components/Resume/ResumePDF/FontSizeContext";

export const TemplateSix = ({
  resume,
  settings,
  isPDF = false,
}: {
  resume: Resume;
  settings: Settings;
  isPDF?: boolean;
}) => {
  const {
    profile,
    workExperiences,
    educations,
    projects,
    skills,
    certifications,
    languages,
    awards,
    custom,
  } = resume;

  const { name } = profile;
  const {
    fontFamily,
    fontSize,
    documentSize,
    formToHeading,
    formToShow,
    formsOrder,
  } = settings;
  const themeColor = settings.themeColor || DEFAULT_FONT_COLOR;

  const showFormsOrder = formsOrder.filter((form) => formToShow[form]);

  const formTypeToComponent: { [type in ShowForm]: () => JSX.Element } = {
    workExperiences: () => (
      <WorkExperience
        heading={formToHeading["workExperiences"]}
        workExperiences={workExperiences}
        themeColor={themeColor}
      />
    ),
    educations: () => (
      <Education
        heading={formToHeading["educations"]}
        educations={educations}
        themeColor={themeColor}
      />
    ),
    projects: () => (
      <Project
        heading={formToHeading["projects"]}
        projects={projects}
        themeColor={themeColor}
        isPDF={isPDF}
      />
    ),
    skills: () => (
      <Skills
        heading={formToHeading["skills"]}
        skills={skills}
        themeColor={themeColor}
      />
    ),
    certifications: () => (
      <Certificate
        heading={formToHeading["certifications"]}
        certifications={certifications}
        themeColor={themeColor}
        isPDF={isPDF}
      />
    ),
    languages: () => (
      <Language
        heading={formToHeading["languages"]}
        languages={languages}
        themeColor={themeColor}
      />
    ),
    awards: () => (
      <Award
        heading={formToHeading["awards"]}
        awards={awards}
        themeColor={themeColor}
      />
    ),
    custom: () => (
      <Custom
        heading={formToHeading["custom"]}
        custom={custom}
        themeColor={themeColor}
      />
    ),
  };

  return (
    <>
      <Document title={`${name} Resume`} author={name} producer={"OpenResume"}>
        <Page
          size={documentSize === "A4" ? "A4" : "LETTER"}
          style={{
            ...styles.flexCol,
            color: DEFAULT_FONT_COLOR,
            fontFamily,
            fontSize: fontSize + "pt",
          }}
        >
          <FontSizeProvider fontSize={fontSize}>
            <View style={{ paddingTop: `${spacing[5]}` }} fixed></View>
            <View
              style={{
                ...styles.flexCol,
                padding: `${spacing[0]} ${spacing[5]}`,
                marginTop: `-${spacing[5]}`,
              }}
            >
              <Profile profile={profile} themeColor={themeColor} isPDF={isPDF} />
              {profile.summary && (
                <Summary
                  heading="SUMMARY"
                  summary={profile.summary}
                  themeColor={themeColor}
                />
              )}
              {showFormsOrder.map((form) => {
                const Component = formTypeToComponent[form];
                return <Component key={form} />;
              })}
            </View>
            <View
              style={{
                width: "100%",
                minHeight: "1px",
                flexGrow: 1,
                flexShrink: 0,
                flexBasis: "auto",
                ...styles.flexRow,
                marginTop: `-${spacing[1]}`,
              }}
            >
              <View style={{ paddingBottom: `${spacing[5]}` }} fixed></View>
            </View>
          </FontSizeProvider>
        </Page>
      </Document>
      <SuppressResumePDFErrorMessage />
    </>
  );
};
